import { Component, Input, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { DomSanitizer } from '@angular/platform-browser';
import { Event } from './events.service';

type ShareLinkType =  'register' | 'join';

@Component({
  selector: 'app-event-share',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatButtonModule],
  template: `
    <button
      mat-raised-button
      color="primary"
      class="share-button"
      (click)="openShare($event)">
      <mat-icon>share</mat-icon>
      Share
    </button>

    <div class="share-menu-overlay" *ngIf="menuOpen" (click)="close()">
      <div class="share-menu" (click)="$event.stopPropagation()" (mouseleave)="close()" >

        <ng-container *ngIf="!pendingUrl">
          <div class="share-menu-header">
            <span>What would you like to share?</span>
            <button mat-icon-button (click)="close()">
              <mat-icon>close</mat-icon>
            </button>
          </div>
          <div class="share-menu-options">
            <button mat-button *ngFor="let type of availableTypes" (click)="pickType(type)">
              <mat-icon>{{ iconFor(type) }}</mat-icon>
              {{ labelFor(type) }}
            </button>
          </div>
        </ng-container>

        <ng-container *ngIf="pendingUrl as url">
          <div class="share-menu-header">
            <span>Share {{ pendingLabel }}</span>
            <button mat-icon-button (click)="close()">
              <mat-icon>close</mat-icon>
            </button>
          </div>
          <div class="share-menu-options">
            <button mat-button (click)="shareToWhatsApp(url)">
              <mat-icon>chat</mat-icon> WhatsApp
            </button>
            <button mat-button (click)="shareToFacebook(url)">
              <mat-icon svgIcon="facebook"></mat-icon> Facebook
            </button>
            <button mat-button (click)="shareToTwitter(url)">
              <mat-icon svgIcon="twitter"></mat-icon> Twitter / X
            </button>
            <button mat-button (click)="shareToLinkedIn(url)">
              <mat-icon svgIcon="linkedin"></mat-icon> LinkedIn
            </button>
            <button mat-button (click)="copyLink(url)">
              <mat-icon>{{ linkCopied ? 'check' : 'content_copy' }}</mat-icon>
              {{ linkCopied ? 'Copied!' : 'Copy Link' }}
            </button>
          </div>
        </ng-container>

      </div>
    </div>
  `,
  styles: [`
    .share-button {
      background-color: #0284c7 !important;
      color: white !important;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
      font-weight: 500;
      width: 100%;

      &:hover {
        background-color: #0369a1 !important;
      }
    }

    .share-menu-overlay {
      position: fixed;
      inset: 0;
      background-color: rgba(0, 0, 0, 0.5);
      z-index: 2100;
      display: flex;
      align-items: flex-end;
      justify-content: center;

      @media (min-width: 600px) {
        align-items: center;
      }
    }

    .share-menu {
      background: white;
      border-radius: 0.75rem 0.75rem 0 0;
      width: 100%;
      max-width: 400px;
      padding: 1rem;

      @media (min-width: 600px) {
        border-radius: 0.75rem;
      }
    }

    .share-menu-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      font-weight: 600;
      margin-bottom: 1rem;
      padding-bottom: 0.75rem;
      border-bottom: 1px solid #e5e7eb;
    }

    .share-menu-options {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;

      button {
        display: flex;
        justify-content: flex-start;
        gap: 0.75rem;
        padding: 0.75rem;
      }
    }
  `]
})
export class EventShareComponent {
  @Input() event!: Event;

  menuOpen = false;
  pendingUrl: string | null = null;
  pendingLabel = '';
  linkCopied = false;

  private iconRegistry = inject(MatIconRegistry);
  private sanitizer = inject(DomSanitizer);

  constructor() {
    // Registering is idempotent per icon name — safe to call even if
    // another instance (e.g. EventsComponent) already registered these.
    this.iconRegistry.addSvgIcon('facebook', this.sanitizer.bypassSecurityTrustResourceUrl('/facebook-svgrepo-com.svg'));
    this.iconRegistry.addSvgIcon('twitter', this.sanitizer.bypassSecurityTrustResourceUrl('/twitter-svgrepo-com.svg'));
    this.iconRegistry.addSvgIcon('linkedin', this.sanitizer.bypassSecurityTrustResourceUrl('/linkedin-linked-in-svgrepo-com.svg'));
  }

get availableTypes(): ShareLinkType[] {
  const types: ShareLinkType[] = [];
  if (this.event.registrationUrl || this.isPublicEvent()) {
    types.push('register');
  }
  if (this.event.meetingUrl) {
    types.push('join');
  }
  return types;
}

  isPublicEvent(): boolean {
    const participants = this.event.participants;
    if (!participants || participants.length === 0) {
      return true;
    }
    return participants.some(p => p?.trim().toLowerCase() === 'public');
  }

  async openShare(evt: MouseEvent): Promise<void> {
    evt.stopPropagation(); // prevents triggering a parent card's click handler

    const types = this.availableTypes;

    if (types.length === 1) {
      await this.attemptNativeShare(types[0]);
      return;
    }

    this.pendingUrl = null;
    this.menuOpen = true;
  }

  async pickType(type: ShareLinkType): Promise<void> {
    await this.attemptNativeShare(type);
  }

  private async attemptNativeShare(type: ShareLinkType): Promise<void> {
    const url = this.urlFor(type);

    if (navigator.share) {
      try {
        await navigator.share({
          title: this.event.title,
          text: this.event.description || `Join us for ${this.event.title}`,
          url
        });
        this.close();
      } catch {
        // user cancelled — ignore
      }
    } else {
      this.pendingUrl = url;
      this.pendingLabel = this.labelFor(type);
      this.menuOpen = true;
    }
  }

  close(): void {
    this.menuOpen = false;
    this.pendingUrl = null;
  }

  urlFor(type: ShareLinkType): string {
    switch (type) {
      case 'register':
       return `${window.location.origin}/events?eventId=${this.event.id}`;
      case 'join':
        return this.event.meetingUrl || '';
      default:
        return `${window.location.origin}/events?eventId=${this.event.id}`;
    }
  }

  labelFor(type: ShareLinkType): string {
    switch (type) {
      case 'register': return 'Registration Link';
      case 'join': return 'Join Meeting Link';
    }
  }

  iconFor(type: ShareLinkType): string {
    switch (type) {
      case 'register': return 'how_to_reg';
      case 'join': return 'launch';
    }
  }

  copyLink(url: string): void {
    navigator.clipboard.writeText(url).then(() => {
      this.linkCopied = true;
      setTimeout(() => {
        this.linkCopied = false;
        this.close();
      }, 1200);
    });
  }

  shareToFacebook(url: string): void {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank', 'width=600,height=400');
    this.close();
  }

  shareToTwitter(url: string): void {
    const text = encodeURIComponent(this.event.title);
    window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${text}`, '_blank', 'width=600,height=400');
    this.close();
  }

  shareToLinkedIn(url: string): void {
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank', 'width=600,height=400');
    this.close();
  }

  shareToWhatsApp(url: string): void {
    const text = encodeURIComponent(`${this.event.title} - ${url}`);
    window.open(`https://wa.me/?text=${text}`, '_blank');
    this.close();
  }
}
